import { ChevronRight } from "lucide-react";
import type { TaskGroup, TaskListProps } from "../types";
import { cn } from "@/lib/utils";

function GroupHeader({ g }: { g: TaskGroup }) {
  if (g.variant === "day") {
    return (
      <div className="mb-1 flex min-h-9 items-baseline gap-2.5 border-t border-border px-1 pt-3">
        <span className="font-display text-[22px] font-bold leading-none tracking-[-0.02em] text-foreground tabular">{g.dayNumber}</span>
        <span className="text-[13.5px] font-medium text-muted-foreground">{g.label}</span>
      </div>
    );
  }
  if (g.variant === "range" || g.variant === "month") {
    return (
      <div className="mb-1 border-t border-border px-1 pt-3">
        <h2 className="text-[16px] font-bold leading-tight tracking-[-0.015em] text-foreground">{g.label}</h2>
      </div>
    );
  }
  if (g.label === null) return null;
  const inner = (
    <>
      {g.collapsible && <ChevronRight className={cn("size-3.5 text-subtle transition-transform duration-150", !g.collapsed && "rotate-90")} />}
      {g.icon}
      <span>{g.label}</span>
      <span className="font-normal text-subtle tabular">{g.tasks.length}</span>
    </>
  );
  return (
    <div className="mb-0.5 flex h-8 items-center gap-2 px-2.5">
      {g.collapsible ? (
        <button
          type="button"
          onClick={g.onToggle}
          aria-expanded={!g.collapsed}
          className="-ml-1.5 inline-flex h-7 items-center gap-1.5 rounded-md px-1.5 text-[13px] font-semibold text-foreground/80 hover:bg-hover hover:text-foreground"
        >
          {inner}
        </button>
      ) : (
        <h2 className="inline-flex items-center gap-1.5 font-sans text-[13px] font-semibold tracking-normal text-foreground/80">{inner}</h2>
      )}
      {g.trailing && <div className="ml-auto flex items-center gap-1">{g.trailing}</div>}
    </div>
  );
}

export function FocusTaskList({ groups, renderTask, empty, footer, listProps }: TaskListProps) {
  const total = groups.reduce((n, g) => n + g.tasks.length, 0);
  const visible = groups.filter((g) => g.tasks.length > 0 || g.showWhenEmpty);
  return (
    <div className="pb-1">
      <div {...listProps} className="outline-none">
        {total === 0 && empty}
        {visible.map((g, i) => {
          const dayish = g.variant === "day" || g.variant === "range" || g.variant === "month";
          return (
            <section
              key={g.key}
              className={cn(i > 0 && (dayish ? "mt-5" : "mt-6"))}
              aria-label={g.variant === "day" ? `${g.dayNumber} ${g.label}` : (g.label ?? undefined)}
            >
              <GroupHeader g={g} />
              {!g.collapsed && <div className="flex flex-col gap-px">{g.tasks.map((t) => renderTask(t))}</div>}
            </section>
          );
        })}
      </div>
      {footer && <div className="mt-3 px-1">{footer}</div>}
    </div>
  );
}
